import type { ReactNode } from "react";
import { Sparkline } from "./Sparkline";
import { compact, ratingColor, signed, type Rating } from "./util";

export interface StatTileProps {
  label: string;
  value: number | string | undefined | null;
  formatValue?: (v: number) => string;
  /**
   * Signed change, shown as a colored arrow and the number: the direction is
   * the arrow's job, so what it is measured against belongs in `title` rather
   * than in a trailing clause on every tile. No arrow when nothing moved.
   * Counts print whole: "+15", never "+15.0".
   */
  delta?: { value: number; digits?: number; suffix?: string; title?: string; upIsGood?: boolean };
  trend?: number[];
  rating?: Rating;
  hero?: boolean;
  footer?: ReactNode;
  unit?: string;
  loading?: boolean;
}

/**
 * Stat tile: label · value (compact, proportional figures) · optional delta,
 * rating (icon + label, never color alone) and 12-point trend sparkline.
 */
export function StatTile({ label, value, formatValue = compact, delta, trend, rating, hero, footer, unit, loading }: StatTileProps) {
  if (loading) {
    return (
      <div className={`card viz-tile ${hero ? "hero" : ""}`} role="group" aria-label={`${label}: loading`} aria-busy="true">
        <span className="label">{label}</span>
        <span className="value">
          <span className="skeleton" style={{ display: "inline-block", width: 64, height: "0.8em" }} />
        </span>
      </div>
    );
  }
  const text = value === undefined || value === null ? "—" : typeof value === "number" ? formatValue(value) : value;
  const dir = delta ? (delta.value > 0 ? "up" : delta.value < 0 ? "down" : "flat") : "flat";
  const good = delta ? (delta.upIsGood ?? true ? delta.value >= 0 : delta.value <= 0) : true;
  return (
    <div className={`card viz-tile ${hero ? "hero" : ""}`} role="group" aria-label={`${label}: ${text}${unit ? ` ${unit}` : ""}`}>
      <span className="label">{label}</span>
      <span className="value">
        {text}
        {unit && <span className="label"> {unit}</span>}
      </span>
      {delta && delta.value !== 0 && (
        <span className={`delta ${good ? "good" : "bad"}`} title={delta.title}>
          <span aria-hidden="true">{dir === "up" ? "▲" : "▼"}</span>
          {signed(delta.value, delta.digits ?? (Number.isInteger(delta.value) ? 0 : 1), delta.suffix ?? "")}
        </span>
      )}
      {rating && (
        <span className="rating">
          <i style={{ background: ratingColor(rating) }} aria-hidden="true" />
          {rating === "good" ? "Good" : rating === "needs-improvement" ? "Needs improvement" : "Poor"}
        </span>
      )}
      {trend && trend.length > 1 && (
        <span className="trend">
          <Sparkline values={trend} label={`${label} trend`} width={120} height={26} formatValue={formatValue} />
        </span>
      )}
      {footer}
    </div>
  );
}
